import { useEffect,useMemo,useState } from "react";
import { Box,Typography,Button,TextField,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,CircularProgress,Switch } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";
import TournamentForm from "./AdminTournaments/TournamentForm";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const formatDate=(value)=>{
  if(!value) return "—";
  const parts=String(value).split("-");
  if(parts.length!==3) return value;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function AdminTournaments(){
  const navigate=useNavigate();
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [tournaments,setTournaments]=useState([]);
  const [search,setSearch]=useState("");
  const [loading,setLoading]=useState(true);
  const [changingStatus,setChangingStatus]=useState("");
  const [formOpen,setFormOpen]=useState(false);
  const [selectedTournament,setSelectedTournament]=useState(null);

  const loadTournaments=async()=>{
    try{
      setLoading(true);

      const response=await fetch("/tournaments/",{
        credentials:"include"
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudieron cargar los torneos."
        );
      }

      setTournaments(Array.isArray(data)?data:data.results||[]);
    }catch(error){
      showError(error.message||"No se pudieron cargar los torneos.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadTournaments();
  },[]);

  const filteredTournaments=useMemo(()=>{
    const value=search.trim().toLowerCase();

    if(!value) return tournaments;

    return tournaments.filter((tournament)=>
      [
        tournament.name,
        tournament.season
      ].some((field)=>
        String(field||"").toLowerCase().includes(value)
      )
    );
  },[tournaments,search]);

  const openCreate=()=>{
    setSelectedTournament(null);
    setFormOpen(true);
  };

  const openEdit=(tournament)=>{
    setSelectedTournament(tournament);
    setFormOpen(true);
  };

  const closeForm=()=>{
    setFormOpen(false);
    setSelectedTournament(null);
  };

  const handleSaved=async(tournament,isEditing)=>{
    closeForm();
    await loadTournaments();

    showSuccess(
      isEditing?
      "Torneo actualizado correctamente.":
      "Torneo creado correctamente."
    );
  };

  const handleActiveChange=async(tournament)=>{
    if(changingStatus) return;

    try{
      setChangingStatus(tournament.slug);

      const response=await fetch(
        `/tournaments/${tournament.slug}/`,
        {
          method:"PATCH",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken")
          },
          body:JSON.stringify({
            active:!tournament.active
          })
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudo actualizar el estado del torneo."
        );
      }

      setTournaments((current)=>
        current.map((item)=>
          item.slug===tournament.slug?
          {
            ...item,
            active:data.active
          }:
          item
        )
      );

      showSuccess(
        data.active?
        "Torneo activado correctamente.":
        "Torneo desactivado correctamente."
      );
    }catch(error){
      showError(
        error.message||
        "No se pudo actualizar el estado del torneo."
      );
    }finally{
      setChangingStatus("");
    }
  };

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1.5,
          mb:2.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{
                xs:typography.sizes.xl,
                md:typography.sizes.pageTitle
              },
              fontWeight:700,
              color:colors.text
            }}
          >
            Torneos
          </Typography>

          <Typography
            sx={{
              mt:0.4,
              fontSize:typography.sizes.md,
              color:colors.textSecondary
            }}
          >
            Administra los torneos y su configuración deportiva.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={openCreate}
          sx={{
            px:2.2,
            textTransform:"none",
            backgroundColor:colors.secondary,
            "&:hover":{
              backgroundColor:colors.secondaryHover
            }
          }}
        >
          Nuevo torneo
        </Button>
      </Box>

      <Box
        sx={{
          mb:1.5,
          display:"flex",
          justifyContent:"flex-end"
        }}
      >
        <TextField
          size="small"
          value={search}
          onChange={(event)=>setSearch(event.target.value)}
          placeholder="Buscar torneo"
          sx={{
            width:{xs:"100%",sm:320},
            "& .MuiOutlinedInput-root":{
              backgroundColor:colors.surface
            }
          }}
        />
      </Box>

      <TableContainer
        sx={{
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radiusLarge,
          backgroundColor:colors.surface,
          boxShadow:"none",
          overflowX:"auto"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{backgroundColor:colors.tableHeader}}>
              <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Torneo
              </TableCell>

              <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Temporada
              </TableCell>

              <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Inicio
              </TableCell>

              <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Finalización
              </TableCell>

              <TableCell align="center" sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Activo
              </TableCell>

              <TableCell align="right" sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading?(
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{py:5}}>
                  <CircularProgress size={26} sx={{color:colors.secondary}} />
                </TableCell>
              </TableRow>
            ):filteredTournaments.length===0?(
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{
                    py:5,
                    color:colors.textSecondary
                  }}
                >
                  No hay torneos registrados.
                </TableCell>
              </TableRow>
            ):(
              filteredTournaments.map((tournament)=>(
                <TableRow
                  key={tournament.slug}
                  hover
                  sx={{
                    "&:last-child td":{
                      borderBottom:0
                    }
                  }}
                >
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize:typography.sizes.md,
                        fontWeight:600,
                        color:colors.text
                      }}
                    >
                      {tournament.name}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize:typography.sizes.md,
                      color:colors.textSecondary
                    }}
                  >
                    {tournament.season||"—"}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize:typography.sizes.md,
                      color:colors.text
                    }}
                  >
                    {formatDate(tournament.starts_on)}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize:typography.sizes.md,
                      color:colors.text
                    }}
                  >
                    {formatDate(tournament.ends_on)}
                  </TableCell>

                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={tournament.active!==false}
                      disabled={changingStatus===tournament.slug}
                      onChange={()=>handleActiveChange(tournament)}
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Box
                      sx={{
                        display:"flex",
                        justifyContent:"flex-end",
                        alignItems:"center",
                        gap:0.5
                      }}
                    >
                      <Button
                        size="small"
                        onClick={()=>navigate(`/admin/tournaments/${tournament.slug}`)}
                        sx={{
                          px:1.2,
                          minWidth:0,
                          textTransform:"none",
                          color:colors.secondary
                        }}
                      >
                        Configurar
                      </Button>

                      <Button
                        size="small"
                        onClick={()=>openEdit(tournament)}
                        sx={{
                          px:1.2,
                          minWidth:0,
                          textTransform:"none",
                          color:colors.secondary
                        }}
                      >
                        Editar
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TournamentForm
        open={formOpen}
        tournament={selectedTournament}
        onClose={closeForm}
        onSaved={handleSaved}
      />
    </Box>
  );
}